import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { Booking } from '../../bookings/entities/Booking.js';
import type { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import type { Contract } from '../../contracts/entities/Contract.js';
import { AGInfos } from '../../contracts/entities/vertrag-sektionen/AGinfos.js';
import { AGSozialVersicherung } from '../../contracts/entities/vertrag-sektionen/AGSozialversicherung.js';
import { AGArbeitszeit } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGArbeitszeit.js';
import type { AGEingruppierung } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import { AGLohnGehaltszahlungen } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGLohngehaltszahlungen.js';
import { AGVertragsInfos } from '../../contracts/entities/vertrag-sektionen/vertrag-eingruppierung/AGvertragsinfos.js';
import { LegacyDebitorNumber } from '../../debitor-numbers/entities/LegacyDebitorNumber.js';
import type { Group } from '../../groups/entities/Group.js';
import { MicrosoftUserData } from '../../microsoft-graph/entities/MicrosoftUserData.js';
import { SepaMandat } from '../../sepa/entities/SepaMandat.js';
import { MitarbeiterDaten } from '../employees/entities/mitarbeiter-daten.js';
import { ProfilDaten } from '../profile/entities/ProfilDaten.js';
import { SchuelerDaten } from '../students/schueler-daten.js';
import { Person } from './person.js';

const subPersonSelectionSet = () =>
  ({
    _id: 1,
    _class: 1,
    email: 1,
    kreditorenNr: 1,
    debitorennummer: 1,
    externalUniqueID: 1,
    oldDebitorennummern: 1,
    nameAbbreviation: 1,
    username: 1,
    legacyDebitorNumbers: selectionSet(() => LegacyDebitorNumber, 1),
    sorgeberechtigteKinderIDs: 1,
    microsoftUserData: {
      microsoftID: 1,
      shareWithMicrosoft: 1,
      manuallyChangedName: 1,
      userPrincipalName: 1,
      mail: 1,
      otherMails: 1,
    },
    roles: {
      name: 1,
      contexts: 1,
    },
    schuelerDaten: {
      schuelerNr: 1,
      heinNummer: 1,
      eingeladeneSorgeberechtigte: 1,
      eingeladeneSpender: 1,
    },
    profilDaten: selectionSet(() => ProfilDaten, 2),
    sepaMandats: selectionSet(() => SepaMandat, 1),
  }) satisfies BaseQLSelectionSet<Person>;

const groupSelectionSet = () =>
  ({
    _id: 1,
    _class: 1,
    name: 1,
    parentIDs: 1,
    kostenstellePrefix: 1,
    kostenstelleSuffix: 1,
    abwesenheitsemail: 1,
    parents: {
      _id: 1,
      _class: 1,
      name: 1,
      // @ts-expect-error on inherited properties
      kostenstellePrefix: 1,
      kostenstelleSuffix: 1,
      abwesenheitsemail: 1,
    },
    // participants: subPersonSelectionSet(),
  }) satisfies BaseQLSelectionSet<Group>;

const agSelectionSet = () =>
  ({
    _id: 1,
    _class: 1,
    agVertragsInfos: {
      ...selectionSet(() => AGVertragsInfos, 1),
      kostenstelleProzentsaetze: {
        _id: 1,
        _class: 1,
        kostenstelleID: 1,
        prozentsatz: 1,
        // @ts-expect-error on inherited properties
        kostenstelle: groupSelectionSet(),
      },
    },
    agLohnGehaltszahlungen: selectionSet(() => AGLohnGehaltszahlungen, 2),
    agArbeitszeit: selectionSet(() => AGArbeitszeit, 1),
    paidWorkTime: 1,
    totalWorkTime: 1,
  }) satisfies BaseQLSelectionSet<AGEingruppierung>;

const contractSelectionSet = () =>
  ({
    _id: 1,
    _class: 1,
    createdAt: 1,
    userID: 1,
    agInfos: selectionSet(() => AGInfos, 1),
    agSozialVersicherung: selectionSet(() => AGSozialVersicherung, 1),
    // @ts-expect-error on inherited properties
    agEingruppierung1: agSelectionSet(),
    // @ts-expect-error on inherited properties
    agEingruppierung2: agSelectionSet(),
    erfahrungsstufeAg1: 1,
    erfahrungsstufeAg2: 1,
    supplements: {
      createdAt: 1,
      nachtragsNummer: 1,
      subNachtragsnummer: 1,
      startDateGeneral: 1,
      endDateGeneral: 1,
      startDateAG1: 1,
      endDateAG1: 1,
      startDateAG2: 1,
      endDateAG2: 1,
      signedAt: 1,
      paragraph: 1,
    },
  }) satisfies BaseQLSelectionSet<Contract>;

const bookingSelectionSet = () =>
  ({
    _id: 1,
    _class: 1,
    startDate: 1,
    endDate: 1,
    userID: 1,
    groupIDs: 1,
    grade: 1,
    classIdentifier: 1,
    createdAt: 1,
    groups: groupSelectionSet(),
  }) satisfies BaseQLSelectionSet<Booking | ClassBooking>;

export const materializedUserSelectionSet = () => {
  const sel: BaseQLSelectionSet<Person> = {
    ...selectionSet(() => Person, 1),
    roles: {
      name: 1,
      contexts: 1,
    },
    microsoftUserData: selectionSet(() => MicrosoftUserData, 1),
    profilDaten: selectionSet(() => ProfilDaten, 2),
    schuelerDaten: selectionSet(() => SchuelerDaten, 1),
    mitarbeiterDaten: selectionSet(() => MitarbeiterDaten, 3),
    sorgeberechtigteKinder: subPersonSelectionSet(),
    sorgeberechtigteEltern: subPersonSelectionSet(),
    spendenKinder: subPersonSelectionSet(),
    spender: subPersonSelectionSet(),
    geschwister: {
      _id: 1,
      _class: 1,
      profilDaten: { stammdaten: { vorname: 1, nachname: 1, geburtsDatum: 1 } },
    },
    buchungen: bookingSelectionSet(),
    bookingsOfChildren: bookingSelectionSet(),
    activeBuchungenOfChildren: bookingSelectionSet(),
    activeBuchungen: bookingSelectionSet(),
    sepaMandats: selectionSet(() => SepaMandat, 1),
    legacyDebitorNumbers: selectionSet(() => LegacyDebitorNumber, 1),
  };

  delete sel.beschaeftigungsumfang1;
  delete sel.beschaeftigungsumfang2;
  delete sel.sepaMandats?.active;
  delete sel.sepaMandats?.billingLocked;
  delete sel.hasLAK;

  return sel;
};
